import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { ComponentRenderMode } from '../../../types/component-skin';
import { CartLogicReturn } from './Cart.types';
import { 
  fetchCartItems, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  removeFromCart,
  addToCart,
  setCartItems,
  selectUser,
  selectWithcookieData
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';

/**
 * 장바구니 컴포넌트 로직 훅
 */
const useCartLogic = (componentData: any, mode: ComponentRenderMode): CartLogicReturn => {
  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Redux 상태
  const { cartItems, loading, error } = useSelector(selectCartState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const withcookieData = useSelector(selectWithcookieData);
  const userInfo = useSelector(selectUser);
  
  // 상태 관리
  const [removeLoading, setRemoveLoading] = useState<boolean>(false);
  const [localQuantities, setLocalQuantities] = useState<Record<number, number>>({});
  const debouncedUpdateRefs = useRef<Record<number, NodeJS.Timeout>>({});
  
  // 옵션 추출
  const {
    title = t("장바구니"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 4000,
    showStock = false
  } = props.componentProps || props;
  
  // 회사 설정에서 무료배송 기준금액 가져오기
  const freeShippingLimit = useMemo(() => {
    try {
      let companySettingJson = null;
      
      if (withcookieData?.company?.companySettingJson) {
        companySettingJson = withcookieData.company.companySettingJson;
      } else if (withcookieData?.skin?.company?.companySettingJson) {
        companySettingJson = withcookieData.skin.company.companySettingJson;
      }
      
      if (typeof companySettingJson === 'string') {
        const parsedSettings = JSON.parse(companySettingJson);
        return parsedSettings.free_shipping_limit || 50000;
      } else if (companySettingJson && typeof companySettingJson === 'object') {
        return companySettingJson.free_shipping_limit || 50000;
      }
      
      return 50000;
    } catch (error) {
      console.error('무료배송 기준금액 가져오기 오류:', error);
      return 50000;
    }
  }, [withcookieData]);
  
  // 등급/직급별 가격 계산 함수 (optionJson priority 기반)
  const calculateLevelPrice = useCallback((basePrice: number, product: any) => {
    // 로그인하지 않은 경우 또는 optionJson이 없는 경우
    if (!userInfo || !isUserLoggedIn || !product?.optionJson?.priority) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    const priority = product.optionJson.priority;
    let levelPrice = basePrice;
    let levelName = null;
    let levelId: string | number | null = null;
    let levelPolicy = null;
    
    // priority에 따라 level1 또는 level2 사용
    if (priority === 'level1' && userInfo.level1) {
      levelId = userInfo.level1;
      levelPolicy = userInfo.level1Policy;
      levelName = levelPolicy?.customName || levelPolicy?.type || '등급 할인';
      
      // level1_price 확인
      if (product.optionJson.level1_price && levelId !== null && product.optionJson.level1_price[levelId]) {
        levelPrice = Number(product.optionJson.level1_price[levelId]) || basePrice;
      }
    } else if (priority === 'level2' && userInfo.level2) {
      levelId = userInfo.level2;
      levelPolicy = userInfo.level2Policy;
      levelName = levelPolicy?.customName || levelPolicy?.type || '직급 할인';
      
      // level2_price 확인
      if (product.optionJson.level2_price && levelId !== null && product.optionJson.level2_price[levelId]) {
        levelPrice = Number(product.optionJson.level2_price[levelId]) || basePrice;
      }
    }
    
    // 개별 가격이 없으면 supply_price_ratio 사용
    if (levelPrice === basePrice && levelPolicy?.settingJson?.supply_price_ratio) {
      const supplyPriceRatio = Number(levelPolicy.settingJson.supply_price_ratio) || 1;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    } else if (levelPrice === basePrice && levelId) {
      // withcookieData에서 정책 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === levelId);
      if (matchingPolicy?.settingJson?.supply_price_ratio) {
        const supplyPriceRatio = Number(matchingPolicy.settingJson.supply_price_ratio) || 1;
        levelPrice = Math.floor(basePrice * supplyPriceRatio);
        if (!levelName && matchingPolicy) {
          levelName = matchingPolicy.customName || matchingPolicy.type || (priority === 'level1' ? '등급 할인' : '직급 할인');
        }
      }
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName: levelId ? levelName : null
    };
  }, [userInfo, isUserLoggedIn, withcookieData]);
  
  // 컴포넌트 마운트 시 장바구니 데이터 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      dispatch(fetchCartItems());
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn]);
  
  // 컴포넌트 언마운트 시 debounced 함수들 정리
  useEffect(() => {
    const currentRefs = debouncedUpdateRefs.current;
    return () => {
      Object.values(currentRefs).forEach(timeoutId => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      });
    };
  }, []);

  // 초기 수량 설정
  useEffect(() => {
    if (cartItems && Array.isArray(cartItems)) {
      const initialQuantities: Record<number, number> = {};
      cartItems.forEach((item: any) => {
        initialQuantities[item.id] = item.count || 1;
      });
      setLocalQuantities(initialQuantities);
    }
  }, [cartItems]);
  
  // API 호출을 위한 debounced 함수 생성
  const updateCartQuantity = useCallback((item: any, newQuantity: number) => {
    // @ts-ignore - Redux Toolkit 타입 오류 무시
    dispatch(addToCart({ 
      product_id: item.productId || item.product.id,
      variant_id: item.variantId || undefined,
      count: newQuantity
    })).catch((error: any) => {
      console.error('수량 변경 중 오류:', error);
      
      // 실패 시 원래 수량으로 롤백
      const originalCount = item.count || 1;
      setLocalQuantities(prev => ({
        ...prev,
        [item.id]: originalCount
      }));
      
      // Redux 스토어도 롤백
      const rollbackItems = cartItems?.map((cartItem: any) => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: originalCount };
        }
        return cartItem;
      });
      if (rollbackItems) {
        dispatch(setCartItems(rollbackItems));
      }
      
      // 에러 알림
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다.'));
      } else {
        const errorMessage = error.message || t('수량 변경에 실패했습니다.');
        alert(t('수량 변경 실패') + ': ' + errorMessage);
      }
    });
  }, [dispatch, cartItems, t]);
  
  // 장바구니 상품 삭제 처리
  const handleRemoveFromCart = useCallback(async (cartItemId: number) => {
    if (!cartItemId) {
      alert(t('삭제할 상품 정보가 올바르지 않습니다.'));
      return;
    }
    
    try {
      setRemoveLoading(true);
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      await dispatch(removeFromCart(cartItemId)).unwrap();
      // @ts-ignore - Redux Toolkit 타입 오류 무시
      await dispatch(fetchCartItems());
      alert(t('상품이 장바구니에서 삭제되었습니다.'));
    } catch (error: any) {
      console.error('장바구니 상품 삭제 중 오류:', error);
      
      if (error.code === 'AUTH_REQUIRED') {
        alert(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동합니다.'));
        window.location.href = '/login';
      } else {
        alert(t('장바구니 상품 삭제에 실패했습니다. 다시 시도해주세요.'));
      }
    } finally {
      setRemoveLoading(false);
    }
  }, [dispatch, t]);
  
  // 수량 증가 처리
  const handleIncreaseQuantity = useCallback((item: any) => {
    const currentCount = item.count || 1;
    const newQuantity = currentCount + 1;
    
    // 로컬 상태 즉시 업데이트
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // Redux 스토어 즉시 업데이트
    if (cartItems && Array.isArray(cartItems)) {
      const updatedCartItems = cartItems.map((cartItem: any) => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: newQuantity };
        }
        return cartItem;
      });
      dispatch(setCartItems(updatedCartItems));
    }
    
    // 기존 timeout이 있으면 취소
    if (debouncedUpdateRefs.current[item.id]) {
      clearTimeout(debouncedUpdateRefs.current[item.id]);
    }
    
    // 새로운 timeout 설정
    debouncedUpdateRefs.current[item.id] = setTimeout(() => {
      updateCartQuantity(item, newQuantity);
    }, 300);
  }, [cartItems, dispatch, updateCartQuantity]);
  
  // 수량 감소 처리
  const handleDecreaseQuantity = useCallback((item: any) => {
    const currentCount = item.count || 1;
    
    if (currentCount <= 1) return;
    
    const newQuantity = currentCount - 1;
    
    // 로컬 상태 즉시 업데이트
    setLocalQuantities(prev => ({
      ...prev,
      [item.id]: newQuantity
    }));
    
    // Redux 스토어 즉시 업데이트
    if (cartItems && Array.isArray(cartItems)) {
      const updatedCartItems = cartItems.map((cartItem: any) => {
        if (cartItem.id === item.id) {
          return { ...cartItem, count: newQuantity };
        }
        return cartItem;
      });
      dispatch(setCartItems(updatedCartItems));
    }
    
    // 기존 timeout이 있으면 취소
    if (debouncedUpdateRefs.current[item.id]) {
      clearTimeout(debouncedUpdateRefs.current[item.id]);
    }
    
    // 새로운 timeout 설정
    debouncedUpdateRefs.current[item.id] = setTimeout(() => {
      updateCartQuantity(item, newQuantity);
    }, 300);
  }, [cartItems, dispatch, updateCartQuantity]);
  
  // 상품 가격 합계 계산
  const calculateTotalPrice = useCallback(() => {
    if (!cartItems || !Array.isArray(cartItems) || cartItems.length === 0) {
      return 0;
    }
    
    return cartItems.reduce((sum: any, item: any) => {
      const basePrice = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 직급별 가격 계산
      const priceInfo = calculateLevelPrice(basePrice, item.product);
      const finalBasePrice = priceInfo.levelPrice;
      
      // Variant 추가 가격 포함
      const additionalPrice = item.variant?.additionalPrice 
        ? Number(item.variant.additionalPrice) 
        : 0;
      
      const totalPrice = finalBasePrice + additionalPrice;
      
      // 로컬 수량을 사용하여 합계 계산
      const quantity = localQuantities[item.id] || item.count || 1;
      return sum + (totalPrice * quantity);
    }, 0);
  }, [cartItems, localQuantities, calculateLevelPrice]);

  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = useCallback(() => {
    const totalPrice = calculateTotalPrice();
    
    // 주문 금액이 무료배송 기준금액보다 크거나 같으면 배송비 무료(0원)
    const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
    
    return totalPrice + appliedShippingFee;
  }, [calculateTotalPrice, shippingFee, freeShippingLimit]);
  
  // 결제 페이지로 이동하는 함수
  const handleCheckout = useCallback(() => {
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('장바구니가 비어 있습니다. 상품을 추가해주세요.'));
      return;
    }
    
    // 결제 페이지로 이동
    navigate('/checkout');
  }, [cartItems, navigate, t]);
  
  // 장바구니 비우기
  const handleClearCart = useCallback(async () => {
    // 확인 다이얼로그
    if (!window.confirm(t('장바구니를 비우시겠습니까?'))) {
      return;
    }
    
    try {
      // 장바구니의 모든 상품에 대해 삭제 요청
      if (cartItems && cartItems.length > 0) {
        setRemoveLoading(true);
        
        // 개별 삭제가 아닌 일괄 삭제로 수정
        for (const item of cartItems) {
          // @ts-ignore - Redux Toolkit 타입 오류 무시
          await dispatch(removeFromCart(item.id)).unwrap();
        }
        
        // 삭제 완료 후 장바구니 목록 다시 불러오기 (한 번만)
        // @ts-ignore - Redux Toolkit 타입 오류 무시
        await dispatch(fetchCartItems());
        alert(t('장바구니가 비워졌습니다.'));
      }
    } catch (error) {
      console.error('장바구니 비우기 중 오류:', error);
      alert(t('장바구니 비우기에 실패했습니다. 다시 시도해주세요.'));
    } finally {
      setRemoveLoading(false);
    }
  }, [cartItems, dispatch, t]);
  
  return {
    data: {
      cartItems,
      loading: loading || removeLoading,
      error,
      localQuantities,
      calculations: {
        subtotal: calculateTotalPrice(),
        shippingFee: calculateTotalPrice() >= freeShippingLimit ? 0 : shippingFee,
        total: calculateGrandTotal()
      },
      isUserLoggedIn,
      isAdminLoggedIn,
      withcookieData,
      userInfo,
      freeShippingLimit,
      title,
      emptyText,
      shippingFee,
      showStock,
      theme: props.theme || {}
    },
    actions: {
      handleIncreaseQuantity,
      handleDecreaseQuantity,
      handleRemoveFromCart,
      handleCheckout,
      handleClearCart,
      calculateLevelPrice,
      calculateTotalPrice,
      calculateGrandTotal
    }
  };
};

export default useCartLogic;